import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { liveQuery } from 'dexie';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../ui/ToastProvider';
import { flushOutbox, pendingOutboxCount } from '../offline/outbox';
import { checkHealth } from '../api/client';

export interface SyncContextValue {
  online: boolean;
  pending: number;
  syncing: boolean;
  lastSync: string | null;
  syncNow: (silent?: boolean) => Promise<void>;
}

const LAST_SYNC_KEY = 'gravenca.lastSync';

export function useSyncEngine(): SyncContextValue {
  const { user, demo } = useAuth();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [browserOnline, setBrowserOnline] = useState(() => navigator.onLine);
  const [apiReachable, setApiReachable] = useState<boolean | null>(null);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(() =>
    window.localStorage.getItem(LAST_SYNC_KEY),
  );
  const attemptedInitialSync = useRef(false);
  const wasOnline = useRef(true);

  // `navigator.onLine` no detecta un backend caído. La sonda /health manda.
  const online = browserOnline && apiReachable !== false;

  useEffect(() => {
    if (!user) {
      setApiReachable(null);
      return;
    }
    if (demo) {
      setApiReachable(null);
      return;
    }

    let cancelled = false;
    let running = false;
    let timer: number | undefined;

    const loop = async () => {
      if (cancelled || running) return;
      running = true;
      if (timer) {
        window.clearTimeout(timer);
        timer = undefined;
      }
      try {
        const reachable = await checkHealth();
        if (cancelled) return;
        setApiReachable(reachable);
        // Mientras no haya backend, sondea cada 5 s para reconectar rápido;
        // con salud confirmada baja a 20 s para no gastar cuota ni batería.
        timer = window.setTimeout(() => void loop(), reachable ? 20_000 : 5_000);
      } finally {
        running = false;
      }
    };

    void loop();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void loop();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [demo, user]);

  useEffect(() => {
    if (!user) {
      setPending(0);
      return;
    }
    const subscription = liveQuery(() => pendingOutboxCount()).subscribe({
      next: setPending,
      error: () => setPending(0),
    });
    return () => subscription.unsubscribe();
  }, [user]);

  const syncNow = useCallback(
    async (silent = false) => {
      if (demo) {
        if (!silent) {
          notify('Modo demostración: los pedidos quedan en la cola local.', 'info');
        }
        return;
      }
      if (!online) {
        if (!silent) notify('Sin conexión. La cola se enviará al reconectar.', 'warning');
        return;
      }

      setSyncing(true);
      try {
        const result = await flushOutbox(false);
        if (result.skipped) {
          if (!silent) notify('No hay movimientos pendientes por sincronizar.', 'info');
          return;
        }

        const stamp = new Date().toISOString();
        window.localStorage.setItem(LAST_SYNC_KEY, stamp);
        setLastSync(stamp);
        await queryClient.invalidateQueries();

        if (!silent) {
          const total = result.aplicados + result.duplicados;
          if (result.errores > 0) {
            notify(
              `${total} movimiento(s) sincronizado(s); ${result.errores} con error.`,
              'warning',
            );
          } else {
            notify(`${total} movimiento(s) sincronizado(s) con el servidor.`, 'success');
          }
        }
      } catch (error) {
        if (!silent) {
          notify(
            error instanceof Error
              ? error.message
              : 'No se pudo sincronizar. Se reintentará al reconectar.',
            'error',
          );
        }
      } finally {
        setSyncing(false);
      }
    },
    [demo, notify, online, queryClient],
  );

  useEffect(() => {
    const handleOnline = () => {
      setBrowserOnline(true);
      void checkHealth().then(setApiReachable);
      void syncNow(true);
    };
    const handleOffline = () => {
      setBrowserOnline(false);
      setApiReachable(false);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncNow]);

  // Al recuperar la sonda de salud se reintenta la cola aunque el navegador
  // nunca haya disparado el evento `online`.
  useEffect(() => {
    if (online && !wasOnline.current) void syncNow(true);
    wasOnline.current = online;
  }, [online, syncNow]);

  // Un refresh de sesión exitoso implica que el backend volvió a responder.
  useEffect(() => {
    const onSessionRestored = () => void syncNow(true);
    window.addEventListener('erp:session-restored', onSessionRestored);
    return () => window.removeEventListener('erp:session-restored', onSessionRestored);
  }, [syncNow]);

  // Al volver a la pestaña, reintenta si quedó algo pendiente.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') void syncNow(true);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [syncNow]);

  useEffect(() => {
    if (demo || !user) return;
    const onServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'ERP_SYNC_REQUEST') void syncNow(true);
    };
    navigator.serviceWorker?.addEventListener('message', onServiceWorkerMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', onServiceWorkerMessage);
    };
  }, [demo, user, syncNow]);

  useEffect(() => {
    if (demo || !user) return;
    const timer = window.setInterval(() => {
      if (navigator.onLine) void syncNow(true);
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [demo, user, syncNow]);

  // Si al abrir la app quedaron movimientos en cola, intenta una vez.
  useEffect(() => {
    if (demo || !user || !online || pending === 0 || attemptedInitialSync.current) return;
    attemptedInitialSync.current = true;
    const timer = window.setTimeout(() => void syncNow(true), 700);
    return () => window.clearTimeout(timer);
  }, [demo, user, online, pending, syncNow]);

  return useMemo(
    () => ({ online, pending, syncing, lastSync, syncNow }),
    [online, pending, syncing, lastSync, syncNow],
  );
}
